/*!

=========================================================
* Vue White Dashboard - v1.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/vue-white-dashboard
* Copyright 2019 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/vue-white-dashboard/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/

import Vue from "vue";
import { store } from "@/store";
import VueRouter from "vue-router";
import "vue-github-buttons/dist/vue-github-buttons.css";
import "@/assets/scss/white-dashboard.scss";
import "@/assets/css/nucleo-icons.css";
import "@/assets/demo/demo.css";
import "@/styles/tokens.scss";

import GlobalComponents from "./globalComponents";
import GlobalDirectives from "./globalDirectives";
import Notify from "@/components/NotificationPlugin";
import { Toast } from "@/util/Toast";
import Endpoints from "@/common/Endpoints";
import i18n from "./i18n";
import SideBar from "@/components/SidebarPlugin";
import Element from "element-ui";
import "element-ui/lib/theme-chalk/index.css";

import * as _ from "lodash";
import axios from "axios";

import Env from "./common/Env";
import App from "./App.vue";
// element ui language configuration
import lang from "element-ui/lib/locale/lang/en";
import locale from "element-ui/lib/locale";
//sentry
import * as Sentry from "@sentry/browser";
import * as Integrations from "@sentry/integrations";
// router setup
import routes from "./router";
import { formatDateTime } from "@/util/DateUtils";
import { CommentState } from "@/common/CommentState";

const setupAxios = () => {
  Vue.prototype.$axios = axios;

  axios.defaults.baseURL = Endpoints.adminApiServerUrl;
  axios.defaults.withCredentials = true;
  axios.defaults.crossDomain = true;
  axios.interceptors.response.use(
    (response) => {
      return response;
    },
    async function (error) {
      if (401 === error.response.status) {
        const redirect = window.location.pathname + window.location.search.replace(/&/g, "|");
        await router.push(`/login?redirect=${redirect}`);
      } else {
        return Promise.reject(error);
      }
    }
  );
};

locale.use(lang);

Vue.config.productionTip = false;

// configure router
const router = new VueRouter({
  routes, // short for routes: routes
  linkExactActiveClass: "active",
  mode: "history",
});
router.beforeEach(async (to, from, next) => {
  if (to.name === "login") {
    next();
    return;
  }
  try {
    const result = await axios.get(`/common/role`);
    const roles = result.data.data.split(",");
    if (to.meta.roles) {
      for (const role of roles) {
        if (to.meta.roles.includes(role)) {
          next();
          return;
        }
      }
      Toast.error("권한이없습니다");
    } else {
      next();
    }
  } catch (e) {
    console.log(e);
  }
});
Vue.use(VueRouter);

Vue.use(Element);
Vue.use(GlobalComponents);
Vue.use(GlobalDirectives);
Vue.use(SideBar);
Vue.use(Notify);

Vue.filter("blipImageUrl", function (fileId) {
  if (_.isNil(fileId)) {
    return null;
  }
  return `${Endpoints.imageServerUrl}${fileId}`;
});

Vue.filter("formatDateTime", function (value) {
  if (!value) return "";
  return formatDateTime(value);
});

Vue.filter("missionPath", function (value) {
  return `${Endpoints.missionUrl}/mission/site/${value}`;
});

Vue.filter("quizPath", function (value) {
  return `${Endpoints.missionUrl}/mission/quiz/${value}`;
});

Vue.filter("examPath", function (value) {
  return `${Endpoints.quizUrl}/mission/exam/${value}`;
});

Vue.filter("photoCardPath", function (value) {
  return `${Endpoints.homepageUrl}/mission/photocard/${value}`;
});

Vue.filter("commentStateName", function (state) {
  switch (state) {
    case CommentState.PRESENT:
      return "표시";
    case CommentState.DELETED_BY_USER:
      return "사용자에 의해 삭제";
    case CommentState.DELETED_BY_ADMIN:
      return "운영자에 의해 삭제";
    default:
      return "Error";
  }
});

Vue.filter("imageUrlWithFileId", function (fileId) {
  if (!fileId) return "";
  return `${Endpoints.imageServerUrl}${fileId}`;
});

Vue.filter("truncate", function (value, length) {
  if (!value) return "";
  value = value.toString();
  if (value.length > length) {
    return value.substring(0, length) + "...";
  } else {
    return value;
  }
});

Vue.filter("reverse", function (value) {
  return value.slice().reverse();
});

window.Toast = Toast;
window.Endpoints = Endpoints;
setupAxios();

if (!Env.isLocal()) {
  Sentry.init({
    dsn: "https://5a59ac6d6c234dde8210b9d68ae1cde4@sentry.io/1865364",
    integrations: [new Integrations.Vue({ Vue, attachProps: true })],
    environment: Env.getCurrentEnv(),
  });
}

const app = new Vue({
  router,
  store,
  i18n,
  render: (h) => h(App),
}).$mount("#app");

console.log(`Running in ${Env.getCurrentEnv()}...`);

export default app;
